<template>
    <Card pageTitle="Loyalty Tiers">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_LOYALTY">
            <div class="tiers-wrap">
                <div class="page-head">
                    <h2>Tier Definitions</h2>
                    <p>Define the visit thresholds that place customers into each loyalty status.</p>
                </div>

                <div class="tier-summary">
                    <div>
                        <span class="summary-label">Active tiers</span>
                        <strong>{{ activeTierCount }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Entry tier</span>
                        <strong>{{ entryTierName }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Rule</span>
                        <strong>One active tier must start at 0 visits</strong>
                    </div>
                </div>

                <table class="tiers-table" v-if="tiers.length">
                    <thead>
                        <tr>
                            <th>Tier name</th>
                            <th>Min lifetime visits</th>
                            <th>Perks</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="tier in tiers" :key="tier.id">
                            <td><CustomInput v-model="tier.name" :disabled="!canEdit" /></td>
                            <td><CustomInput type="number" :step="1" v-model="tier.min_visits" :disabled="!canEdit" /></td>
                            <td><CustomInput v-model="tier.perks" :disabled="!canEdit" /></td>
                            <td>
                                <span :class="['status-badge', tier.active ? 'ok' : 'neutral']">
                                    {{ tier.active ? "Active" : "Inactive" }}
                                </span>
                            </td>
                            <td>
                                <div class="row-actions">
                                    <Button v-if="canEdit" @click="saveTier(tier)">Save</Button>
                                    <Button v-if="canEdit && tier.active" borderOnly @click="deactivateTier(tier)">Deactivate</Button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-else class="empty">No tiers configured.</p>

                <div v-if="canEdit" class="create-box">
                    <h3>Add tier</h3>
                    <div class="grid">
                        <CustomInput placeholder="Name" v-model="newTier.name" />
                        <CustomInput type="number" :step="1" placeholder="Min visits" v-model="newTier.min_visits" />
                        <CustomInput placeholder="Perks" v-model="newTier.perks" />
                    </div>
                    <Button @click="createTier">Add tier</Button>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { errMsg } from "../../utils/common"

export default {
    components: { Card, Button, CustomInput, AllowPermission },
    data() {
        return {
            PERMISSION_CODES,
            tiers: [],
            newTier: { name: "", min_visits: 0, perks: "" },
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        activeTierCount() {
            return this.tiers.filter((tier) => tier.active !== false).length
        },
        entryTierName() {
            const tier = this.tiers.find((item) => item.active !== false && Math.round(Number(item.min_visits) || 0) === 0)
            return tier ? tier.name : "Missing"
        },
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_LOYALTY,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
    },
    async mounted() {
        await this.loadTiers()
    },
    methods: {
        async loadTiers() {
            try {
                this.tiers = (await LoyaltyFetchApi.fetchTiers()) || []
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load tiers"))
            }
        },
        validateTierList(pendingTier = null) {
            const tiers = pendingTier
                ? this.tiers.map((tier) => (tier.id === pendingTier.id ? pendingTier : tier))
                : this.tiers
            const activeTiers = tiers.filter((tier) => tier.active !== false)
            if (!activeTiers.some((tier) => Math.round(Number(tier.min_visits) || 0) === 0)) {
                return "At least one active tier must start at 0 visits"
            }
            const names = new Set()
            const visits = new Set()
            for (const tier of tiers) {
                const name = String(tier.name || "").trim().toLowerCase()
                const minVisits = Math.round(Number(tier.min_visits) || 0)
                if (!name) return "Tier name is required"
                if (minVisits < 0) return "Min visits must be non-negative"
                if (names.has(name)) return "Tier names must be unique"
                if (visits.has(minVisits)) return "Min visits must be unique"
                names.add(name)
                visits.add(minVisits)
            }
            return ""
        },
        validateNewTier() {
            const nextTier = {
                id: "__new",
                name: this.newTier.name,
                min_visits: Math.max(0, Math.round(Number(this.newTier.min_visits) || 0)),
                active: true,
            }
            const tiers = [...this.tiers, nextTier]
            if (!tiers.some((tier) => tier.active !== false && Math.round(Number(tier.min_visits) || 0) === 0)) {
                return "At least one active tier must start at 0 visits"
            }
            const names = new Set()
            const visits = new Set()
            for (const tier of tiers) {
                const name = String(tier.name || "").trim().toLowerCase()
                const minVisits = Math.round(Number(tier.min_visits) || 0)
                if (!name) return "Tier name is required"
                if (names.has(name)) return "Tier names must be unique"
                if (visits.has(minVisits)) return "Min visits must be unique"
                names.add(name)
                visits.add(minVisits)
            }
            return ""
        },
        async saveTier(tier) {
            const error = this.validateTierList(tier)
            if (error) {
                this.$toast.error(error)
                return
            }
            try {
                await LoyaltyFetchApi.updateTier(tier.id, {
                    name: tier.name,
                    min_visits: Math.max(0, Math.round(Number(tier.min_visits) || 0)),
                    perks: tier.perks || null,
                })
                this.$toast.success("Tier saved")
                await this.loadTiers()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not save tier"))
            }
        },
        async deactivateTier(tier) {
            const pendingTier = { ...tier, active: false }
            const error = this.validateTierList(pendingTier)
            if (error) {
                this.$toast.error(error)
                return
            }
            try {
                await LoyaltyFetchApi.deactivateTier(tier.id)
                await this.loadTiers()
                this.$toast.success("Tier deactivated")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not deactivate tier"))
            }
        },
        async createTier() {
            const error = this.validateNewTier()
            if (error) {
                this.$toast.error(error)
                return
            }
            try {
                await LoyaltyFetchApi.createTier({
                    name: this.newTier.name.trim(),
                    min_visits: Math.max(0, Math.round(Number(this.newTier.min_visits) || 0)),
                    perks: this.newTier.perks || null,
                })
                this.newTier = { name: "", min_visits: 0, perks: "" }
                await this.loadTiers()
                this.$toast.success("Tier added")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not add tier"))
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.tiers-wrap { max-width: 980px; }
.page-head { margin-bottom: 20px; }
.page-head h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: #0b302c; }
.page-head p { margin: 0; color: #5f6368; line-height: 1.45; }
.tier-summary { display: grid; grid-template-columns: 140px 160px 1fr; gap: 12px; margin-bottom: 18px; }
.tier-summary > div { border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px; background: #fff; }
.tier-summary strong { display: block; margin-top: 4px; color: #0b302c; }
.summary-label { color: #80868b; font-size: 12px; font-weight: 700; text-transform: uppercase; }
.tiers-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
.tiers-table th, .tiers-table td { text-align: left; padding: 12px 8px; border-bottom: 1px solid #eee; vertical-align: middle; }
.create-box { border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px; }
.create-box h3 { font-weight: 600; margin-bottom: 12px; }
.grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 12px; }
.empty { color: #80868b; }
.row-actions { display: flex; gap: 8px; justify-content: flex-end; }
.status-badge { display: inline-flex; align-items: center; border-radius: 999px; padding: 3px 8px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.status-badge.ok { background: #e8f5ec; color: #1f7a3b; }
.status-badge.neutral { background: #eef0f2; color: #4b5563; }
@media (max-width: 900px) {
    .tier-summary { grid-template-columns: 1fr; }
    .grid { grid-template-columns: 1fr; }
}
</style>
